import { Button, Progress, Table } from "antd";
import React, { useMemo } from "react";
import { EyeTwoTone, HistoryOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { SupervisionWarehouse } from "../../../../../models/Supervision";
import { useLanguage } from "../../../../../context/LanguageContext";
import { getWarehouseReferenceColor } from "../../../../../common/warehouseColorReferences";

interface SupervisionRefactorTableProps {
  supervisionWarehouses: SupervisionWarehouse[];
}

const SupervisionRefactorTable: React.FC<SupervisionRefactorTableProps> = ({
  supervisionWarehouses,
}) => {
  const { language } = useLanguage();

  const progressPercentTextStyle: React.CSSProperties = {
    color: "#686565",
    fontSize: "12px",
    fontWeight: 500,
  };

  const getEffectiveColor = (effectivePercentage: number): string => {
    if (effectivePercentage > 0.75) {
      return "#1677ff";
    }

    if (effectivePercentage > 0.5) {
      return "#ebd230";
    }

    return "#f5222d";
  };

  const columns = useMemo(
    () => [
      {
        title: language === "es" ? "Depósito" : "Warehouse",
        dataIndex: "warehouse_name",
        key: "warehouse_name",
        width: "10%",
        render: (value: SupervisionWarehouse["warehouse_name"]) => {
          const color = getWarehouseReferenceColor(value);
          return (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: color,
                  display: "inline-block",
                }}
              />
              <span style={{ color, fontWeight: 600 }}>{value}</span>
            </span>
          );
        },
      },
      {
        title: language === "es" ? "Número de Conteo" : "Counting Number",
        dataIndex: "counting_number",
        key: "counting_number",
        align: "center" as const,
        width: "15%",
        render: (value: SupervisionWarehouse["counting_number"]) => {
          if (value === null || value === undefined) {
            return "-";
          }

          return String(value).trim() === "" ? "-" : value;
        },
      },
      {
        title: language === "es" ? "Cantidad de Dispositivos" : "Devices Counting",
        dataIndex: "devices_count",
        key: "devices_count",
        align: "center" as const,
        width: "10%",
      },
      {
        title: language === "es" ? "Lecturas" : "Readings",
        key: "readings",
        width: "24%",
        render: (_: unknown, record: SupervisionWarehouse) => (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <Progress
              type="line"
              percent={parseFloat((record.reading_percentage * 100).toFixed(2))}
              format={(percent) => (
                <span style={progressPercentTextStyle}>{`${percent}%`}</span>
              )}
              status="active"
              strokeWidth={10}
              style={{ width: "300px", margin: 0 }}
            />
            <div style={{ fontSize: "13px" }}>
              <span style={{ color: "#1677ff", fontWeight: 600 }}>
                {record.read_positions}
              </span>
              <span style={{ color: "#8c8c8c", margin: "0 4px" }}>/</span>
              <span style={{ color: "#8c8c8c", fontWeight: 400 }}>
                {record.total_count}
              </span>
            </div>
          </div>
        ),
      },
      {
        title: language === "es" ? "Efectividad" : "Effective",
        key: "effective",
        width: "24%",
        render: (_: unknown, record: SupervisionWarehouse) => (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <Progress
              type="line"
              percent={parseFloat((record.effective_percentage * 100).toFixed(2))}
              format={(percent) => (
                <span style={progressPercentTextStyle}>{`${percent}%`}</span>
              )}
              status="active"
              strokeWidth={10}
              style={{ width: "300px", margin: 0 }}
              strokeColor={getEffectiveColor(record.effective_percentage)}
            />
            <div style={{ fontSize: "13px" }}>
              <span
                style={{
                  color: getEffectiveColor(record.effective_percentage),
                  fontWeight: 600,
                }}
              >
                {record.effective_readings}
              </span>
              <span style={{ color: "#8c8c8c", margin: "0 4px" }}>/</span>
              <span style={{ color: "#8c8c8c", fontWeight: 400 }}>
                {record.read_positions}
              </span>
            </div>
          </div>
        ),
      },
      {
        title: language === "es" ? "Historial" : "History",
        key: "history",
        align: "center" as const,
        width: "6%",
        render: (_: unknown, record: SupervisionWarehouse) => (
          <Link to={`history/${record.warehouse_idx}`}>
            <Button type="link" icon={<HistoryOutlined />} />
          </Link>
        ),
      },
      {
        title: language === "es" ? "Detalles" : "Details",
        key: "details",
        align: "center" as const,
        width: "6%",
        render: (_: unknown, record: SupervisionWarehouse) =>
          record.is_processing ? (
            <Link to={`${record.warehouse_idx}`} state={{ supervisionWarehouse: record }}>
              <Button type="link" icon={<EyeTwoTone />} />
            </Link>
          ) : (
            <Button type="link" icon={<EyeTwoTone />} disabled />
          ),
      },
    ],
    [language]
  );

  return (
    <Table
      columns={columns}
      dataSource={supervisionWarehouses}
      tableLayout="fixed"
      rowKey="warehouse_idx"
      pagination={false}
      locale={{
        emptyText:
          language === "es"
            ? "No hay supervisiones disponibles"
            : "No supervisions available",
      }}
    />
  );
};

export default SupervisionRefactorTable;
